import type { JobStage } from '@/lib/api'

// Standard roles-table row + header, shared by the Jobs list (generalized) and the Client dashboard
// (client-specific) so both read identically: ID · Role · Skills · Stage · Shortlisted.
export interface RoleTableItem {
  id: string
  title: string
  company?: string | null
  systemRef: string | null
  ref: string | null
  status: string
  stage: JobStage
  screened: boolean
  screenedCount?: number // total AI-screened in the latest run (top of the funnel)
  shortlistedCount?: number // recruiter-selected of those (bottom of the funnel)
  shortlistCount?: number // legacy single count (client dashboard) — fallback
  mustHave: string[]
  niceToHave: string[]
}

// Ordered delivery pipeline + display metadata (dot + chip colours). 'archived' sits off-pipeline.
export const STAGE_ORDER: JobStage[] = ['sourcing', 'screening', 'shortlisted', 'submitted', 'interview', 'placement']
export const STAGE_META: Record<JobStage, { label: string; chip: string; dot: string }> = {
  sourcing: { label: 'Sourcing', chip: 'border-border bg-muted/50 text-muted-foreground', dot: 'bg-muted-foreground/60' },
  screening: { label: 'Screening', chip: 'border-info/40 bg-info/10 text-info', dot: 'bg-info' },
  shortlisted: { label: 'Shortlisted', chip: 'border-teal-400/40 bg-teal-400/10 text-teal-600', dot: 'bg-teal-500' },
  submitted: { label: 'Submitted', chip: 'border-warning/40 bg-warning/10 text-warning', dot: 'bg-warning' },
  interview: { label: 'Interview', chip: 'border-violet-400/40 bg-violet-400/10 text-violet-500', dot: 'bg-violet-500' },
  placement: { label: 'Placement', chip: 'border-success/40 bg-success/10 text-success', dot: 'bg-success' },
  archived: { label: 'Archived', chip: 'border-border bg-muted/40 text-muted-foreground/70', dot: 'bg-muted-foreground/40' },
}

export function StageBadge({ stage }: { stage: JobStage }) {
  const m = STAGE_META[stage] ?? STAGE_META.sourcing
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${m.chip}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

export function RolesHead() {
  return (
    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
      <th className="px-4 py-2.5 font-medium">ID</th>
      <th className="px-4 py-2.5 font-medium">Role</th>
      <th className="px-4 py-2.5 font-medium">Skills</th>
      <th className="px-4 py-2.5 font-medium">Stage</th>
      <th className="px-4 py-2.5 font-medium">Funnel</th>
    </tr>
  )
}

export function RoleRow({ role, showCompany, onClick }: { role: RoleTableItem; showCompany?: boolean; onClick: () => void }) {
  return (
    <tr onClick={onClick} className="group cursor-pointer transition-colors hover:bg-muted/40">
      <td className="whitespace-nowrap px-4 py-2.5 align-top">
        <div className="font-mono text-xs text-foreground">{role.systemRef ?? '—'}</div>
        {role.ref && <div className="font-mono text-[11px] text-muted-foreground">{role.ref}</div>}
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium text-foreground group-hover:underline">{role.title}</div>
        {showCompany && <div className="text-xs text-muted-foreground">{role.company || '—'}</div>}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex max-w-[320px] flex-wrap gap-1">
          {role.mustHave.map((s) => (
            <span key={s} className="inline-flex items-center rounded border border-info/40 bg-info/10 px-1.5 py-0.5 text-[11px] text-info">{s}</span>
          ))}
          {role.niceToHave.slice(0, 3).map((s) => (
            <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">{s}</span>
          ))}
          {role.niceToHave.length > 3 && <span className="px-1 text-[11px] text-muted-foreground">+{role.niceToHave.length - 3}</span>}
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-2.5">
        <StageBadge stage={role.stage} />
      </td>
      <td className="whitespace-nowrap px-4 py-2.5">
        {!role.screened ? (
          <span className="text-muted-foreground">—</span>
        ) : role.screenedCount != null ? (
          <div className="flex flex-col gap-0.5 text-xs leading-tight tabular-nums">
            <span className="text-muted-foreground">{role.screenedCount} screened</span>
            <span className="font-medium text-teal-600">{role.shortlistedCount ?? 0} shortlisted</span>
          </div>
        ) : (
          <span className="text-xs tabular-nums text-muted-foreground">{role.shortlistCount ?? 0} shortlisted</span>
        )}
      </td>
    </tr>
  )
}
